import { WidgetEnv } from "@xapp/stentor-chat-widget";
import React, { FC, useContext, useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import { ChatConfigContext, Config, defaultServerUrl, useConnectionInfo } from "../../config";
import { useOpenUrlCallback } from "../../hooks/useOpenUrlCallback";
import { MessageMiddleware } from "../../middlewares";
import { executeAction, reset, sendFile, sendMessage, sendTyping } from "../../store/actions";
import { writeMessage } from "../../store/actions/writeMessage";
import { CONNECTION_STATUS_TYPE } from "../../store/ChatAction";
import { ChatState } from "../../store/ChatState";
import { checkSessionExpiration } from "../../utils/checkSessionExpiration";
import { getConfigurableMessages } from "../../utils/configurableMessages";
import { useChatDispatch } from "../../utils/useChatDispatch";
import { useChatServerDispatch } from "../../utils/useChatServerDispatch";
import useExternalScript from "../../utils/useExternalScript";
import useGreeting from "../../utils/useGreeting";
import { ChatServerOptions } from "../../xapp/ChatServer";
import { ChatMessageRequest } from "../../xapp/ChatServerMessage";
import { ChatServerContext, useChatServer } from "../../xapp/XappChat";
import { ChatButton } from "../ChatButton";
import { ChatFooter } from "../ChatFooter";
import { MessageList } from "../MessageList";
import { ServerOffline } from "../ServerOffline";
import { ChatHeader } from "../ChatHeader";
import { WidgetStylesheet } from "../WidgetStylesheet";
import { ErrorOverlay } from "../ErrorOverlay";
import { WsButton } from "../WsButton";
import { WS_BUTTON_DISMISS_ANIMATION_DURATION } from "../WsButton/constants";
import "./ChatWidget.scss";
import RefreshConfirmationModal from "./ModalContent";
import setVisualStatus from "../../store/actions/setVisualStatus";
import setSessionId from "../../store/actions/setSessionId";
import { log, err } from "../../utils";

/**
 * Props for the ChatWidget component.
 *
 * This interface defines all available configuration options for the chat widget,
 * including display modes, configuration loading strategies, and event callbacks.
 */
export interface ChatWidgetProps {
    /**
     * Enable pre-chat form before starting the conversation.
     * When enabled, users must fill out a form (name, email, etc.) before accessing chat.
     *
     * @default false
     * @example
     * ```tsx
     * <ChatWidget preChatFormEnabled={true} config={myConfig} />
     * ```
     */
    readonly preChatFormEnabled?: boolean;

    /**
     * Widget environment configuration containing server connection details, theme, and behavior settings.
     *
     * This is the primary configuration object that defines how the widget connects to your backend,
     * how it appears visually, and how it behaves. Can be provided directly or via the `getConfig` callback.
     *
     * **Note:** If both `config` and `getConfig` are provided, `getConfig` takes precedence.
     *
     * @see WidgetEnv for the full configuration schema
     * @example
     * ```tsx
     * const config: WidgetEnv = {
     *   connection: {
     *     serverUrl: 'wss://chat.example.com',
     *     type: 'websocketraw'
     *   },
     *   channelId: 'support-channel',
     *   theme: {
     *     header: { background: { color: '#007AFF' } }
     *   }
     * };
     *
     * <ChatWidget config={config} />
     * ```
     */
    readonly config?: WidgetEnv;

    /**
     * Display mode for the widget.
     *
     * - **`'normal'`** (default): Floating chat button that expands into a chat window.
     *   User can minimize/maximize. Best for desktop websites.
     *
     * - **`'docked'`**: Always visible, docked to the side or bottom of the screen.
     *   Cannot be minimized. Best for mobile apps and WebViews.
     *
     * - **`'static'`**: Embedded inline in the page content, takes up fixed space.
     *   Cannot be minimized or moved. Best for dedicated chat pages.
     *
     * @default 'normal'
     * @example
     * ```tsx
     * // Docked mode for mobile
     * <ChatWidget config={config} mode="docked" />
     *
     * // Static mode for dedicated chat page
     * <ChatWidget config={config} mode="static" />
     * ```
     */
    readonly mode?: ChatWidgetMode;

    /**
     * Custom middleware for processing and transforming chat messages before rendering.
     *
     * Middleware allows you to intercept messages and render custom components for specific
     * message types or modify message content. Useful for adding custom UI elements,
     * formatting, or handling special message types.
     *
     * @see MessageMiddleware
     * @example
     * ```tsx
     * const customMiddleware = (next) => (props) => {
     *   const { msg } = props;
     *
     *   // Handle custom message type
     *   if (msg.type === 'SPECIAL_CARD') {
     *     return <CustomCard data={msg.data} />;
     *   }
     *
     *   // Pass to next middleware
     *   return next(props);
     * };
     *
     * <ChatWidget
     *   config={config}
     *   messageMiddleware={customMiddleware}
     * />
     * ```
     */
    readonly messageMiddleware?: MessageMiddleware;

    /**
     * Callback fired when the connection status changes.
     *
     * Use this to monitor the widget's connection state and display custom UI or
     * handle connection issues. Connection status changes throughout the widget lifecycle
     * as it connects, disconnects, reconnects, or encounters errors.
     *
     * @param status - Current connection status
     *   - `'online'`: Successfully connected to server
     *   - `'offline'`: Disconnected from server
     *   - `'pending'`: Connecting or reconnecting (loading state)
     *
     * @example
     * ```tsx
     * const [status, setStatus] = useState('pending');
     *
     * <ChatWidget
     *   config={config}
     *   onConnectionStatusChange={(newStatus) => {
     *     setStatus(newStatus);
     *     if (newStatus === 'offline') {
     *       alert('Chat is currently unavailable');
     *     }
     *   }}
     * />
     * ```
     */
    onConnectionStatusChange?(status: CONNECTION_STATUS_TYPE): void;

    /**
     * Optional async callback to load configuration dynamically.
     *
     * When provided, the widget will wait for this Promise to resolve before initializing.
     * This prevents race conditions where the widget attempts to connect before configuration
     * is available. Particularly useful for:
     *
     * - **React Native apps**: Where props may propagate with delays across the native bridge
     * - **API-based config**: When configuration needs to be fetched from a server
     * - **Dynamic config**: When config depends on user authentication or other async operations
     * - **Native modules**: When waiting for React Native native modules to initialize
     *
     * While the Promise is pending, the widget displays a loading spinner. If the Promise
     * rejects, an error message is shown to the user.
     *
     * **Important:** If both `config` and `getConfig` are provided, `getConfig` takes precedence
     * and the `config` prop is ignored.
     *
     * @returns Promise that resolves to the WidgetEnv configuration
     * @throws Error if configuration cannot be loaded (will be displayed to user)
     *
     * @example
     * ```tsx
     * // Basic usage - fetch from API
     * <ChatWidget
     *   getConfig={async () => {
     *     const response = await fetch('/api/chat-config');
     *     return response.json();
     *   }}
     * />
     * ```
     *
     * @example
     * ```tsx
     * // React Native - wait for native module
     * import { NativeModules } from 'react-native';
     *
     * <ChatWidget
     *   getConfig={async () => {
     *     await NativeModules.ChatModule.initialize();
     *     return await NativeModules.ChatModule.getConfig();
     *   }}
     * />
     * ```
     *
     * @example
     * ```tsx
     * // Wait for authentication with error handling
     * <ChatWidget
     *   getConfig={async () => {
     *     // Wait for auth
     *     while (!window.auth?.user) {
     *       await new Promise(resolve => setTimeout(resolve, 100));
     *     }
     *
     *     // Fetch config with user context
     *     try {
     *       const config = await fetchConfigForUser(window.auth.user);
     *       return config;
     *     } catch (error) {
     *       throw new Error('Failed to load chat configuration. Please try again.');
     *     }
     *   }}
     * />
     * ```
     *
     * @example
     * ```tsx
     * // With timeout protection
     * <ChatWidget
     *   getConfig={async () => {
     *     const timeout = new Promise((_, reject) =>
     *       setTimeout(() => reject(new Error('Config load timeout')), 10000)
     *     );
     *
     *     const configPromise = fetch('/api/config').then(r => r.json());
     *
     *     return Promise.race([configPromise, timeout]);
     *   }}
     * />
     * ```
     */
    getConfig?(): Promise<WidgetEnv>;
}

/**
 * Props for ChatWidgetWrapper component.
 * Extends ChatWidgetProps with the same interface.
 */
export interface ChatWidgetWrapperProps extends ChatWidgetProps {}

/**
 * Display modes for the chat widget.
 *
 * - **`'normal'`**: Default floating button mode - chat button appears as a floating action button,
 *   clicking it opens the chat window. User can minimize/maximize the window.
 *
 * - **`'docked'`**: Always-visible docked mode - widget is permanently visible and docked to the
 *   side or bottom of the screen. Cannot be minimized. Ideal for mobile apps and WebViews.
 *
 * - **`'static'`**: Static embedded mode - widget is embedded inline in the page content as a
 *   fixed element. Cannot be minimized or repositioned. Ideal for dedicated chat pages.
 */
export type ChatWidgetMode = "normal" | "docked" | "static";

const ChatWidgetWrapper: FC<ChatWidgetWrapperProps> = (props) => {
    const [rawConfig, setRawConfig] = useState<WidgetEnv | undefined>(props.config);
    const [configLoading, setConfigLoading] = useState<boolean>(!!props.getConfig);
    const [configError, setConfigError] = useState<Error | undefined>();

    // Load config from callback if provided
    useEffect(() => {
        let cancelled = false;

        if (props.getConfig) {
            setConfigLoading(true);
            setConfigError(undefined);

            props
                .getConfig()
                .then((config) => {
                    if (!cancelled) {
                        setRawConfig(config);
                        setConfigLoading(false);
                        log("[ChatWidget] Config loaded from getConfig callback");
                    }
                })
                .catch((error) => {
                    if (!cancelled) {
                        setConfigError(error);
                        setConfigLoading(false);
                        err(`[ChatWidget] Failed to load config: ${error.message}`);
                    }
                });
        } else if (props.config) {
            // If no callback, use the config prop directly
            setRawConfig(props.config);
            setConfigLoading(false);
        }

        return () => {
            cancelled = true;
        };
        // Only depend on getConfig and config - not the entire props object to avoid infinite loops
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.getConfig, props.config]);

    const connection = useConnectionInfo(rawConfig);

    const config = useMemo<Config>(
        () => ({
            ...rawConfig,
            connection,
            assetUrl: connection?.serverUrl ?? defaultServerUrl,
            env: rawConfig,
        }),
        [connection, rawConfig]
    );

    const token = useSelector<ChatState, string | null>((state) => state.connection.token);

    const options = useMemo<ChatServerOptions>(() => {
        let configurableMessages = getConfigurableMessages();

        if (
            rawConfig?.configurableMessages &&
            Array.isArray(rawConfig.configurableMessages.items) &&
            rawConfig.configurableMessages.items.length > 0
        ) {
            configurableMessages = rawConfig.configurableMessages;
        }

        return {
            token,
            bot: {
                nick: "Bot",
                displayName: rawConfig?.botName,
                avatarPath: rawConfig?.avatarUrl,
            },
            configurableMessages,
            hooks: rawConfig?.hooks,
        };
    }, [token, rawConfig]);

    // Only create chat server when config is ready (not loading and no error)
    const chatServer = useChatServer(
        configLoading || configError ? null : connection,
        configLoading || configError ? null : options
    );

    // Determine mode class for loading/error states
    const mode = props.mode ?? "normal";
    const modeClass = `widget-container--${mode}`;

    // Show loading state while config is being fetched
    if (configLoading) {
        return (
            <div className={`widget-container widget-container--loading ${modeClass}`}>
                <div className="xa-spinner-container visible">
                    <div className="xa-spinner" />
                </div>
            </div>
        );
    }

    // Show error state if config failed to load
    if (configError) {
        return (
            <div className={`widget-container widget-container--error ${modeClass}`}>
                <div className="widget-error-message">
                    Failed to load chat configuration: {configError.message}
                </div>
            </div>
        );
    }

    return (
        <ChatConfigContext.Provider value={config}>
            <ChatServerContext.Provider value={chatServer}>
                <ChatWidget {...props} config={rawConfig} />
            </ChatServerContext.Provider>
        </ChatConfigContext.Provider>
    );
};

const ChatWidget: FC<ChatWidgetProps> = (props) => {
    const innerDispatch = useChatDispatch();
    const dispatch = useChatServerDispatch();

    // From Redux
    const chatState = useSelector<ChatState, ChatState>((state) => state);

    // Refresh modalReference
    const modalRef = useRef<HTMLDivElement>({} as HTMLDivElement);

    const mode = props.mode ?? "normal";
    const dockedMode = mode === "docked";
    const staticMode = mode === "static";
    const modeClass = `widget-container--${mode}`;

    const canRefresh = props.config.header?.actions?.refresh;
    // can't minimize in docked mode or static mode.
    const canMinimize = !dockedMode && !staticMode && props.config.header?.actions?.minimize;
    log(
        `docked: ${dockedMode} static: ${staticMode} minimized: ${props.config.header?.actions?.minimize}`
    );

    let canCancel: boolean;
    // To preserve legacy behavior, cancel needs a little more checks
    if (typeof props.config.header?.actions?.cancel === "boolean") {
        canCancel = !dockedMode && !staticMode && props.config.header?.actions?.cancel;
    } else {
        canCancel = !dockedMode && !staticMode;
    }

    // For backward compatibility. Note: the action will create the actual "visuals" object" (this is a copy).
    if (!chatState.visuals) {
        chatState.visuals = {};
    }

    // Our state - pull from storage
    const [visible, setVisibleState] = useState<boolean>(
        (!canMinimize && !canCancel) ||
            // !!get("visible") ||
            chatState.visuals.visible ||
            (props.config?.autoOpenOnWidth &&
                window.matchMedia(`(min-width: ${props.config?.autoOpenOnWidth})`).matches)
    );
    const [typing, setTypingState] = useState<boolean>(false); // false initially

    const chatServer = useContext(ChatServerContext);

    const patternsConfig = props.config?.autoOpenOnPattern?.patterns;

    const currentUrl = window.location.href;

    const patternExist = patternsConfig && patternsConfig.length > 0;

    const patternMatches = patternsConfig?.some((pattern) => currentUrl.includes(pattern));

    const configWidth = props.config?.autoOpenOnPattern?.minimumWidth;

    // eslint-disable-next-line no-restricted-globals
    const currentWidth = screen.width;

    const setVisible = useCallback(
        (newVisible: boolean) => {
            if (staticMode) {
                return;
            }
            log(`setVisible: ${newVisible}`);
            setVisibleState(newVisible);
            innerDispatch(
                setVisualStatus({
                    visible: newVisible,
                })
            );
        },
        [innerDispatch, staticMode]
    );

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            const body = document.getElementsByTagName("body")[0];
            body.tabIndex = -1;
            if (event.key === "Escape") {
                body.focus();
            }
        };

        document.addEventListener("keydown", handleKeyDown);

        if (
            checkSessionExpiration(
                chatState?.sessionExpiration || props.config?.sessionExpiration,
                chatState?.chats[chatState.chats.length - 1]?.timestamp,
                chatState?.lastTimestamp
            )
        ) {
            innerDispatch(setSessionId(undefined));
            innerDispatch(reset());
        }

        return () => {
            document.removeEventListener("keydown", handleKeyDown);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [isTabVisible, setIsTabVisible] = useState(!document.hidden);

    useEffect(() => {
        const handleVisibilityChange = () => {
            setIsTabVisible(!document.hidden);
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);

        return () => {
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, []);

    useEffect(() => {
        // Too early?
        if (!chatServer) {
            log("Tab visibility changed but no chatServer yet");
            return;
        }

        if (!isTabVisible) {
            log("Tab is HIDDEN - calling sleep()");
            chatServer.sleep();
        } else {
            log("Tab is VISIBLE - calling wakeup()");
            chatServer.wakeup();
        }
    }, [chatServer, innerDispatch, isTabVisible]);

    useEffect(() => {
        // For reopen widget after move on same window
        // if (get("opened")) {
        //     setVisible(true);
        // }

        if (chatState.visuals.opened) {
            setVisible(true);
        } else {
            if (mode === "normal") {
                setVisible(false);
            }
        }

        if (patternExist && patternMatches) {
            setVisible(true);
        }

        if (currentWidth < +configWidth) {
            setVisible(false);
        }
    }, [
        currentWidth,
        patternExist,
        patternMatches,
        configWidth,
        setVisible,
        chatState.visuals.opened,
        mode,
    ]);

    function handleOnChange() {
        if (!typing) {
            dispatch(sendTyping(true));
            setTypingState(true);
        }

        stopTyping();
    }

    function stopTyping() {
        if (!typing) return;
        dispatch(sendTyping(false));
        setTypingState(false);
    }

    function handleSendMessage(msg: ChatMessageRequest) {
        dispatch(sendMessage(msg));
    }

    function handleWriteMessage(msg: ChatMessageRequest) {
        innerDispatch(writeMessage(msg.msg, msg.user));
    }

    const handleOpenUrl = useOpenUrlCallback();

    function handleOnSubmit(rawInput: string) {
        // Don't allow visitor to send msg if not chatting
        if (chatState.accountStatus === "offline" && !chatState.isChatting) return;

        // Don't send empty messages
        if (!rawInput) return;

        // Immediately stop typing
        stopTyping();

        chatServer.flush();

        dispatch(executeAction(rawInput));
    }

    function handleFileUpload(event: React.DragEvent<HTMLInputElement>) {
        event.preventDefault();

        // Only send the first file dropped on input
        const file = event?.dataTransfer?.files[0];

        if (!file) {
            return;
        }

        dispatch(sendFile(file));
    }

    function getVisibilityClass() {
        return visible ? "visible" : "";
    }

    function openRestartConfirmationModal() {
        // Interesting, this call the modalRef of the restart modal
        modalRef.current.style.display = "block";
    }

    function closeRestartConfirmationModal() {
        modalRef.current.style.display = "none";
    }

    function handleReset() {
        innerDispatch(reset());
    }

    function handleRestartClick() {
        if (config.header.actions.refreshShowConfirmation) {
            // show the modal
            openRestartConfirmationModal();
            // we will close when
        } else {
            handleReset();
        }
    }

    function handleRestartModalCloseClick() {
        closeRestartConfirmationModal();
    }

    /** Called when minimize button is clicked */
    function handleMinimizeClick() {
        innerDispatch(
            setVisualStatus({
                opened: false,
                hasInteracted: true,
            })
        );
        setVisible(false);
    }

    /** Called when cancel is clicked */
    function handleCancelClick() {
        // First reset to clear all state
        innerDispatch(reset());
        // Then set hasInteracted to prevent CTA from showing after cancel
        innerDispatch(
            setVisualStatus({
                opened: false,
                hasInteracted: true,
            })
        );
        setVisible(false);
    }

    function chatButtonOnClick() {
        innerDispatch(
            setVisualStatus({
                opened: true,
                hasInteracted: true,
            })
        );
        setVisible(true);
        setTimeout(() => {
            document.getElementById("chatWidgetInput").focus();
        }, 100);
    }

    function handleCtaDismiss() {
        innerDispatch(
            setVisualStatus({
                hasInteracted: true,
            })
        );
    }

    function handleWsButtonPress(buttonId: string) {
        log(`WS Button pressed: ${buttonId}`);

        // If the button has spinning behavior, update state to show spinner
        if (chatState.wsButton?.pressBehavior === "spinning") {
            // Update the button state to show it's pressed
            innerDispatch({
                type: "ws_button_display",
                detail: {
                    ...chatState.wsButton,
                    isPressed: true,
                    timestamp: +new Date()
                }
            });
        }

        // Send the button pressed event to the server
        if (chatServer && chatServer.sendButtonPressed) {
            chatServer.sendButtonPressed(buttonId).catch((error) => {
                err(`Failed to send button press: ${error}`);
                // Reset button state on error
                if (chatState.wsButton) {
                    innerDispatch({
                        type: "ws_button_display",
                        detail: {
                            ...chatState.wsButton,
                            isPressed: false,
                            timestamp: +new Date()
                        }
                    });
                }
            });
        }

        // If the button has disabled behavior, animate out then dismiss
        if (chatState.wsButton?.pressBehavior === "disabled") {
            // First set dismissing state to trigger animation
            innerDispatch({
                type: "ws_button_display",
                detail: {
                    ...chatState.wsButton,
                    isDismissing: true,
                    timestamp: +new Date()
                }
            });

            // Then actually dismiss after animation completes
            setTimeout(() => {
                innerDispatch({
                    type: "ws_button_dismiss",
                    detail: {
                        id: buttonId,
                        timestamp: +new Date()
                    }
                });
            }, WS_BUTTON_DISMISS_ANIMATION_DURATION);
        }
    }

    const isOffline = chatState.accountStatus === "offline" && !chatState.isChatting;
    const messages = chatState && chatState.chats;

    log(`Rendering - accountStatus: "${chatState.accountStatus}", connectionStatus: "${chatState.connection.connectionStatus}"`);

    const { config, onConnectionStatusChange } = props;

    useGreeting(!isOffline && !props.preChatFormEnabled && visible);

    const connectionStatus = chatState.connection.connectionStatus;
    useEffect(() => {
        if (onConnectionStatusChange) {
            onConnectionStatusChange(connectionStatus);
        }
    }, [connectionStatus, onConnectionStatusChange]);

    useExternalScript(props.config?.middlewareUrl);

    // This is a pseudo agent. It represent's the widget (shown in the header avatar for instance)
    const widgetAgent = chatState.agents["agent:robot"]?.user ||
        config?.agent || {
            nick: "agent:robot",
            avatarPath: config.avatarUrl,
            display_name: "Agent",
        };

    return (
        <>
            <div className={`widget-container ${modeClass} ${getVisibilityClass()}`}>
                <WidgetStylesheet theme={config?.theme} />
                <ChatHeader
                    accountStatus={chatState.accountStatus}
                    refreshOnClick={handleRestartClick}
                    minimizeOnClick={handleMinimizeClick}
                    cancelOnClick={handleCancelClick}
                    agent={widgetAgent}
                    canRefresh={canRefresh}
                    canMinimize={canMinimize}
                    canCancel={canCancel}
                    config={config?.header}
                    menuConfig={config.menu}
                    onSubmit={handleOnSubmit}
                />
                <MessageList
                    visible={visible}
                    queuePosition={chatState.queuePosition}
                    isChatting={chatState.isChatting}
                    isOffline={isOffline}
                    messages={messages}
                    agents={chatState.agents}
                    agent={config?.agent}
                    lastRatingRequestTimestamp={chatState.lastRatingRequestTimestamp}
                    hasRating={chatState.hasRating}
                    visitorId={chatState.visitorId}
                    hasWsButton={!!chatState.wsButton}
                    messageMiddleware={props.messageMiddleware}
                    textTypingStatusEnabled={props.config?.typingStatus?.textTypingStatusEnabled}
                    onSend={handleSendMessage}
                    onWrite={handleWriteMessage}
                    onOpenUrl={handleOpenUrl}
                    minimizeOnClick={handleMinimizeClick}
                />
                <div
                    className={`xa-spinner-container ${
                        visible && connectionStatus === "pending" ? "visible" : ""
                    }`}>
                    <div className="xa-spinner" />
                </div>
                {connectionStatus === "offline" && <ServerOffline />}
                {chatState.wsButton && visible && (
                    <WsButton
                        button={chatState.wsButton}
                        onPress={handleWsButtonPress}
                    />
                )}
                <ChatFooter
                    isAdmin={config?.isAdmin}
                    isChatting={chatState.isChatting}
                    placeholder={config?.input?.placeholder}
                    sendButtonIcon={config?.footer?.sendButton?.icon}
                    sendButtonIconHover={config?.footer?.sendButton?.iconHover}
                    sendButtonIconDisabled={config?.footer?.sendButton?.iconDisabled}
                    visible={visible}
                    hasWsButton={!!chatState.wsButton}
                    menuConfig={props.config.menu}
                    footerConfig={props.config?.footer}
                    inputConfig={props.config?.input}
                    onChange={handleOnChange}
                    onSubmit={handleOnSubmit}
                    onFileUpload={handleFileUpload}
                />
                <div className="restartModal" ref={modalRef} onClick={handleRestartModalCloseClick}>
                    <RefreshConfirmationModal
                        onClose={handleRestartModalCloseClick}
                        onReset={handleReset}
                    />
                </div>
            </div>
            <ChatButton
                addClass={getVisibilityClass()}
                onClick={chatButtonOnClick}
                config={config?.cta}
                imageUrl={config?.chatButton?.imageUrl}
                visible={visible}
                hasInteracted={chatState.visuals?.hasInteracted}
                onCtaDismiss={handleCtaDismiss}
            />
            <ErrorOverlay enableErrorOverlay={config?.enableErrorOverlay} />
        </>
    );
};

export default ChatWidgetWrapper;
